import { Flex, Group, Loader, Stack, Text, Title } from "@mantine/core";
import { createFileRoute } from "@tanstack/react-router";
import { IconPencil, IconPlus } from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";

import type { RouterContextType } from "@/api/permissions/permissions.api";
import {
  getStorageUnits,
  StorageUnitQueryKeys,
  type StorageUnit,
  leaseStateFor,
} from "@/api/procurement/procurement.storageUnits";
import { StorageUnitLeaseDecisionModal } from "@/components/features/Procurement/design/StorageUnitLeaseDecisionModal";
import { StorageUnitFormModal } from "@/components/features/Procurement/design/StorageUnitFormModal";
import { StorageUnitRecord } from "@/components/features/Procurement/design/StorageUnitRecord";
import { Button } from "@/components/ui/Button/Button";
import { useActiveSession } from "@/hooks/useActiveSession";
import { authGuardWithPermissions } from "@/utils/authGuard";

import styles from "./procurement.module.scss";

export const Route = createFileRoute("/_authenticated/procurement/storage-units")({
  component: StorageUnitsPage,
  beforeLoad: async (args) => {
    await authGuardWithPermissions({
      context: args.context as RouterContextType,
      location: args.location as { pathname: string },
    });
  },
});

/*
 * A storage unit's tenant_id is a non-null foreign key, so a caller with no entity context has no
 * tenant to create one in and the API refuses the write. A platform admin can still read every
 * tenant's units, which is why the list works and the button would not.
 *
 * The way in is impersonation from User Management, which is how a super admin enters an entity
 * everywhere else in this app; procurement does not get its own mechanism for it. So the message
 * names that path rather than only saying the write is unavailable - a control that fails is bad,
 * and a message that does not say what to do instead is barely better.
 */
/* Mantine colour tokens for the three lease states, matching the notification bell's severities. */
const LEASE_TONE: Record<"expired" | "urgent" | "soon", string> = {
  expired: "var(--text-danger)",
  urgent: "var(--text-warning)",
  soon: "dimmed",
};

const NO_ENTITY_HELP =
  "A unit belongs to one entity, and this session is not inside one. Open User Management and impersonate a user in the entity to add or change units.";

const EMPTY_TITLE = "No storage units yet";
const EMPTY_BODY =
  "A unit appears here once an officer records one against a storage request. It then stays, with its lease and access list, after that request is finished.";

/**
 * SERV-2663 AC1: a unit has to be findable without going through the request that created it,
 * which is the whole reason it is a record rather than attributes on a request. So this is a plain
 * list of the tenant's units, and selecting one shows the record.
 */
function StorageUnitsPage() {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  /* null closed, "new" creating, an id editing that unit. */
  const [decisionTarget, setDecisionTarget] = useState<string | null>(null);
  const [formTarget, setFormTarget] = useState<string | null>(null);
  const { tenantId } = useActiveSession();
  const canWrite = Boolean(tenantId);

  const { data: units = [], isLoading } = useQuery({
    queryKey: [StorageUnitQueryKeys.list],
    queryFn: ({ signal }) => getStorageUnits(signal),
  });

  const selected: StorageUnit | null =
    units.find((unit) => unit.id === selectedId) ?? units[0] ?? null;

  return (
    <Flex direction="column" className={styles.container}>
      <Group justify="space-between" align="center" className={styles.header}>
        <Title order={3} className={styles.title}>
          Storage Units
        </Title>
        {canWrite ? (
          <Button
            variant="primary"
            size="small"
            iconLeft={<IconPlus size={16} />}
            onClick={() => setFormTarget("new")}
          >
            Add storage unit
          </Button>
        ) : (
          <Text size="sm" c="dimmed" maw="52ch" ta="right">
            {NO_ENTITY_HELP}
          </Text>
        )}
      </Group>

      {isLoading ? (
        <Flex justify="center" p="var(--spacing-x-large)">
          <Loader size="sm" />
        </Flex>
      ) : units.length === 0 ? (
        <Stack gap="var(--spacing-2x-small)">
          <Text fw="var(--font-weight-semi-bold)">{EMPTY_TITLE}</Text>
          <Text size="sm" c="dimmed" maw="60ch">
            {EMPTY_BODY}
          </Text>
          {canWrite ? (
            <div>
              <Button
                variant="primary"
                size="small"
                iconLeft={<IconPlus size={16} />}
                onClick={() => setFormTarget("new")}
              >
                Add the first unit
              </Button>
            </div>
          ) : null}
        </Stack>
      ) : (
        <Flex gap="var(--spacing-medium)" align="flex-start" wrap="wrap">
          <Stack gap="var(--spacing-2x-small)" miw={220}>
            {units.map((unit) => (
              <button
                key={unit.id}
                type="button"
                className={styles.unitListRow}
                aria-current={selected?.id === unit.id}
                onClick={() => setSelectedId(unit.id)}
              >
                <Text fw="var(--font-weight-semi-bold)" size="sm">
                  {unit.unitNumber}
                </Text>
                <Text size="xs" c="dimmed">
                  {unit.facilityName}
                </Text>
                {/*
                 * SERV-2666 AC3: on the row, not only on the selected unit. A lease that ran out
                 * weeks ago otherwise looks like a perfectly normal active unit in this list, and
                 * the list is what an officer scans.
                 */}
                {(() => {
                  const lease = leaseStateFor(unit);
                  return lease ? (
                    <Text size="xs" c={LEASE_TONE[lease.severity]}>
                      {lease.label}
                    </Text>
                  ) : null;
                })()}
              </button>
            ))}
          </Stack>
          {selected ? (
            <Stack gap="var(--spacing-small)" style={{ flex: 1, minWidth: 0 }}>
              {canWrite ? (
                <Flex justify="flex-end">
                  {/*
                   * SERV-2666 AC2: the decision is offered where the lease is read, and only when
                   * there is one to make - a lease more than a week out has nothing to decide, and
                   * a vacated unit has nothing left to decide about. A unit already waiting on a
                   * dispatcher photo keeps the action, because that is the step in progress.
                   */}
                  {leaseStateFor(selected) || selected.status === "PENDING_VACATE" ? (
                    <Button
                      variant="primary"
                      size="small"
                      onClick={() => setDecisionTarget(selected.id)}
                    >
                      {selected.status === "PENDING_VACATE"
                        ? "Continue vacating"
                        : "Extend or vacate"}
                    </Button>
                  ) : null}
                  <Button
                    variant="secondary"
                    size="small"
                    iconLeft={<IconPencil size={16} />}
                    onClick={() => setFormTarget(selected.id)}
                  >
                    Edit unit
                  </Button>
                </Flex>
              ) : null}
              {/* SERV-3101: key resets reveal state per unit, so a refusal or codes never carry over. */}
              <StorageUnitRecord key={selected.id} unit={selected} />
            </Stack>
          ) : null}
        </Flex>
      )}

      {decisionTarget
        ? (() => {
            const unit = units.find((candidate) => candidate.id === decisionTarget);
            return unit ? (
              <StorageUnitLeaseDecisionModal unit={unit} onClose={() => setDecisionTarget(null)} />
            ) : null;
          })()
        : null}

      {formTarget ? (
        <StorageUnitFormModal
          unit={formTarget === "new" ? null : (units.find((u) => u.id === formTarget) ?? null)}
          onClose={() => setFormTarget(null)}
        />
      ) : null}
    </Flex>
  );
}
